import React, { useState, useEffect } from 'react';
import { Search, Download, Pencil, ArrowUpRight, Clock, AlertCircle, CheckCircle2, DollarSign, Eye, Trash2, Loader2, RefreshCcw, Filter, X, Calendar, Package, ArrowRight, ChevronDown, ArrowUpDown, MoreHorizontal, Copy } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { sendInvoiceEmail } from '@/lib/email';
import { defaultTemplates } from '@/lib/emailTemplates';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getInvoices, updateInvoice, getCompanySettings, deleteInvoice, getCustomer, getProducts } from '@/lib/api';
import type { Invoice } from '@/types/firebase';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { useEffectiveCompanyId } from '@/hooks/useEffectiveCompanyId';
import { showNotification } from '@/lib/generalNotifications';
import { InvoicePreview } from '@/components/InvoicePreview';
import { collection, addDoc, doc, getDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CreateInvoiceButton } from '@/components/CreateInvoiceButton';
import { InvoiceActivityTimeline } from '@/components/InvoiceActivityTimeline';
import { InvoiceEmailDialog } from '@/components/InvoiceEmailDialog';
import { processRefund as processNMIRefund } from '@/lib/nmi';
import { EnhancedTable, TableBody, TableCell, TableHeader, TableHead, TableRow, TableContainer } from '@/components/ui/enhanced-table';
import { LoadingCard, LoadingTable } from '@/components/ui/loading';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { InvoiceTemplatesTable } from '@/components/InvoiceTemplatesTable';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// Temporarily use simple versions of toggle-group and tooltip until we can create proper components
const ToggleGroup = ({ type, value, className, children }: any) => (
  <div className={`flex flex-wrap ${className}`}>{children}</div>
);

const ToggleGroupItem = ({ value, size, variant, onClick, className, children }: any) => (
  <button 
    type="button" 
    className={`px-3 py-1 mx-0.5 rounded-md text-xs ${variant === 'default' ? 'bg-primary text-white' : 'bg-transparent text-gray-700 border border-gray-300'} ${className}`}
    onClick={onClick}
  >
    {children}
  </button>
);

// Let's also add a RepeatIcon since it was missing
const RepeatIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m17 2 4 4-4 4" /><path d="M3 11v-1a4 4 0 0 1 4-4h14" /><path d="m7 22-4-4 4-4" /><path d="M21 13v1a4 4 0 0 1-4 4H3" />
  </svg>
);

const invoiceTableHeaderClass = 'text-[11px] font-semibold uppercase tracking-wide text-slate-600';

// Update the InvoiceWithTransaction interface
interface InvoiceWithTransaction extends Omit<Invoice, 'status'> {
  paymentTransactionId?: string;
  metadata?: {
    wasRefunded?: boolean;
    refundDate?: string;
    isRecurring?: boolean;
    statusOverride?: 'refunded' | 'cancelled' | string;
    [key: string]: any;
  };
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
  description?: string;
}

// Helper to parse 'YYYY-MM-DD' date strings consistently
const parseDateString = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  
  try {
    // Parse the date string directly to avoid timezone conversion issues
    const [year, month, day] = dateStr.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      // Invalid date
      return new Date(); // Return today as fallback
    }
    
    // Create a date object with local timezone components and set to noon to avoid date shifting
    return new Date(year, month - 1, day, 12, 0, 0);
  } catch (error) {
    // Error parsing date
    return new Date(); // Return today as fallback
  }
};

// Get days past due for an overdue invoice
const getDaysPastDue = (invoice: Invoice): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = parseDateString(invoice.dueDate);
  return Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
};

// Get days till due for a pending invoice
const getDaysTillDue = (invoice: Invoice): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = parseDateString(invoice.dueDate);
  return Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const getInvoiceStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'paid':
      return 'bg-green-600 text-white';
    case 'overdue':
      return 'bg-red-600 text-white';
    case 'sent':
    case 'pending':
      return 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20';
    case 'refunded':
      return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
    case 'cancelled':
      return 'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20';
    case 'draft':
    default:
      return 'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20';
  }
};

// Add a helper function to check if platform fee should be applied
const shouldApplyPlatformFee = (invoice: Invoice, companySettings: any | null | undefined) => {
  // Don't apply platform fee if it's explicitly set to 0 on the invoice
  if (invoice.platformFee === 0) {
    return false;
  }
  
  // Don't apply platform fee if the invoice was paid with a bank account/ACH
  if (invoice.status === 'paid' && (
    invoice.paymentMethod === 'ach' || 
    (invoice as InvoiceWithTransaction).metadata?.paymentMethod === 'ach'
  )) {
    return false;
  }
  
  // Otherwise apply platform fee if it's enabled in settings and percentage is > 0
  return !!(companySettings?.platformFee && companySettings.platformFee > 0);
};

// Update the dateRange type definition with more options
type DateRangeType = 'all' | '30days' | '60days' | '90days' | 'ytd' | 'mtd' | 'qtd' | 'custom';

// Helper to format 'YYYY-MM-DD' as local date
const formatLocalDate = (dateStr: string) => {
  if (!dateStr) return '';
  try {
    // Parse the date string directly to avoid timezone conversion issues
    const [year, month, day] = dateStr.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      // Invalid date
      return '';
    }
    
    // Create a date object with local timezone components and set to noon to avoid date shifting
    const date = new Date(year, month - 1, day, 12, 0, 0);
    
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  } catch (error) {
    // Error formatting date
    return '';
  }
};

export default function Invoices() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'paid' | 'overdue' | 'pending'>('all');
  const [showRecurring, setShowRecurring] = useState(false);
  const [showInstallment, setShowInstallment] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [isProcessingRefund, setIsProcessingRefund] = useState(false);
  const [invoiceToRefund, setInvoiceToRefund] = useState<InvoiceWithTransaction | null>(null);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [hasProducts, setHasProducts] = useState<boolean | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeType>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [amountRange, setAmountRange] = useState<'all' | 'under1k' | '1kto5k' | 'over5k'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { effectiveCompanyId } = useEffectiveCompanyId();
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'status' | 'customer'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedDateRange, setSelectedDateRange] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'standard' | 'recurring' | 'installment'>('standard');
  
  // Set the active tab based on the URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'recurring' || tabParam === 'installment') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Prefetch products data as soon as company context is available
  useEffect(() => {
    if (effectiveCompanyId) {
      // Prefetch products data to prevent UI flashing
      queryClient.prefetchQuery({
        queryKey: ['products', effectiveCompanyId],
        queryFn: () => getProducts(effectiveCompanyId || ''),
        staleTime: 300000, // 5 minutes
      });
    }
  }, [effectiveCompanyId, queryClient]);

  // Fetch company settings
  const { data: companySettings } = useQuery({
    queryKey: ['companySettings', effectiveCompanyId],
    queryFn: () => getCompanySettings(effectiveCompanyId as string),
    enabled: !!effectiveCompanyId
  });

  // Fetch data with polling every 30 seconds
  const { data: invoices = [], isLoading: isLoadingInvoices, refetch: refetchInvoices } = useQuery({
    queryKey: ['invoices', effectiveCompanyId],
    queryFn: () => {
      console.log('[Invoices.tsx] Fetching invoices for companyId:', effectiveCompanyId);
      if (!effectiveCompanyId) {
        console.error('[Invoices.tsx] ERROR: Attempting to fetch without companyId!');
        return [];
      }
      return getInvoices(effectiveCompanyId);
    },
    enabled: !!effectiveCompanyId,
    refetchInterval: 30000, // Poll every 30 seconds
    refetchIntervalInBackground: false, // Stop polling when tab is not active
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Refetch when component mounts
    staleTime: 0, // Always consider data stale to ensure fresh data
    gcTime: 0 // Don't cache at all - always fetch fresh
  });

  // Fetch products to check if any exist
  const { data: products = [], isLoading: isLoadingProducts, isSuccess: productsQuerySuccess } = useQuery({
    queryKey: ['products', effectiveCompanyId],
    queryFn: () => getProducts(effectiveCompanyId || ''),
    enabled: !!effectiveCompanyId,
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  // Update hasProducts state when products query completes
  useEffect(() => {
    if (productsQuerySuccess) {
      setHasProducts(products.length > 0);
    }
  }, [productsQuerySuccess, products]);

  // Refetch invoices when returning to this page
  useEffect(() => {
    // Focus event ensures we get fresh data when returning from other pages
    const handleFocus = () => {
      console.log('Invoices page focused, refetching invoices...');
      refetchInvoices();
    };

    window.addEventListener('focus', handleFocus);
    
    // Also refetch immediately when component mounts
    refetchInvoices();
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetchInvoices]);

  // Check for past due invoices and update them
    const checkForPastDueInvoices = async () => {
    try {
      const now = new Date(); // Use the current exact time
      const updatedInvoices: string[] = [];
      
      // Check pending invoices for past due
      const pendingInvoices = invoices.filter(invoice => invoice.status === 'sent');
        
      for (const invoice of pendingInvoices) {
        // Parse the due date properly to avoid timezone issues
        const dueDate = parseDateString(invoice.dueDate);
        
        // Add 24 hours to the due date (grace period)
        const dueDatePlus24Hours = new Date(dueDate.getTime() + 24 * 60 * 60 * 1000);
        
        // If the due date + 24 hours is in the past, mark as overdue
        if (dueDatePlus24Hours < now && !updatedInvoices.includes(invoice.id)) {
          try {
          await updateInvoice(invoice.id, { status: 'overdue' });
            updatedInvoices.push(invoice.id);
          } catch (updateError) {
            // Failed to update invoice to overdue
          }
        }
      }
      
      if (updatedInvoices.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['invoices', effectiveCompanyId] });
        // Marked invoices as overdue
      }
    } catch (error) {
      // Error checking for past due invoices
      }
    };

  // Calculate metrics
  const metrics = {
    total: invoices.length,
    totalAmount: invoices.reduce((sum, invoice) => {
      const baseTotalAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
      const platformFeeAmount = shouldApplyPlatformFee(invoice, companySettings) ? 
        baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
      return sum + baseTotalAmount + platformFeeAmount;
    }, 0),
    paid: invoices.filter(invoice => invoice.status === 'paid').length,
    paidAmount: invoices.filter(invoice => invoice.status === 'paid').reduce((sum, invoice) => {
      const baseTotalAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
      const platformFeeAmount = shouldApplyPlatformFee(invoice, companySettings) ? 
        baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
      return sum + baseTotalAmount + platformFeeAmount;
    }, 0),
    overdue: invoices.filter(invoice => invoice.status === 'overdue').length,
    overdueAmount: invoices.filter(invoice => invoice.status === 'overdue').reduce((sum, invoice) => {
      const baseTotalAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
      const platformFeeAmount = shouldApplyPlatformFee(invoice, companySettings) ? 
        baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
      return sum + baseTotalAmount + platformFeeAmount;
    }, 0),
    overdue30: invoices.filter(invoice => invoice.status === 'overdue' && getDaysPastDue(invoice) <= 30).reduce((sum, invoice) => {
      const baseTotalAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
      const platformFeeAmount = shouldApplyPlatformFee(invoice, companySettings) ? 
        baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
      return sum + baseTotalAmount + platformFeeAmount;
    }, 0),
    overdue60: invoices.filter(invoice => invoice.status === 'overdue' && getDaysPastDue(invoice) > 30 && getDaysPastDue(invoice) <= 60).reduce((sum, invoice) => {
      const baseTotalAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
      const platformFeeAmount = shouldApplyPlatformFee(invoice, companySettings) ? 
        baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
      return sum + baseTotalAmount + platformFeeAmount;
    }, 0),
    overdue90Plus: invoices.filter(invoice => invoice.status === 'overdue' && getDaysPastDue(invoice) > 60).reduce((sum, invoice) => {
      const baseTotalAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
      const platformFeeAmount = shouldApplyPlatformFee(invoice, companySettings) ? 
        baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
      return sum + baseTotalAmount + platformFeeAmount;
    }, 0),
    pending: invoices.filter(invoice => invoice.status === 'sent').length,
    pending30: invoices.filter(invoice => invoice.status === 'sent' && getDaysTillDue(invoice) <= 30).length,
    pending60: invoices.filter(invoice => invoice.status === 'sent' && getDaysTillDue(invoice) > 30 && getDaysTillDue(invoice) <= 60).length,
    pending90Plus: invoices.filter(invoice => invoice.status === 'sent' && getDaysTillDue(invoice) > 60).length,
    recurring: invoices.filter(invoice => (invoice as InvoiceWithTransaction).metadata?.isRecurring).length,
    recurringAmount: invoices.filter(invoice => (invoice as InvoiceWithTransaction).metadata?.isRecurring).reduce((sum, invoice) => {
      const baseTotalAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
      const platformFeeAmount = shouldApplyPlatformFee(invoice, companySettings) ? 
        baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
      return sum + baseTotalAmount + platformFeeAmount;
    }, 0),
  };

  // Mutations
  const updateInvoiceMutation = useMutation({
    mutationFn: ({ id, invoice }: { id: string; invoice: Partial<Invoice> }) => 
      updateInvoice(id, invoice),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices', effectiveCompanyId] });
      showNotification({
        title: "Success",
        message: "Invoice updated successfully",
        type: "success"
      });
    }
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: (id: string) => deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices', effectiveCompanyId] });
      showNotification({
        title: "Success",
        message: "Invoice deleted successfully",
        type: "success"
      });
    },
    onError: (error) => {
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete invoice",
        type: "error"
      });
    }
  });

  const handleFilterToggle = (value: string) => {
    if (value === 'all' || value === 'paid' || value === 'overdue' || value === 'pending') {
      setActiveFilter(value as any);
    } else if (value === 'recurring') {
      setShowRecurring(!showRecurring);
    } else if (value === 'installment') {
      setShowInstallment(!showInstallment);
    }
    
    // Update active filters for badge display
    const newFilters = [...activeFilters];
    const filterIndex = newFilters.indexOf(value);
    
    if (filterIndex === -1) {
      newFilters.push(value);
    } else {
      newFilters.splice(filterIndex, 1);
    }
    
    setActiveFilters(newFilters);
  };

  const removeFilter = (filter: string) => {
    if (filter === 'all' || filter === 'paid' || filter === 'overdue' || filter === 'pending') {
      setActiveFilter('all');
    } else if (filter === 'recurring') {
      setShowRecurring(false);
    } else if (filter === 'installment') {
      setShowInstallment(false);
    }
    
    const newFilters = activeFilters.filter(f => f !== filter);
    setActiveFilters(newFilters);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Modify the filter method
  const filteredInvoices = invoices.filter((invoice) => {
    // Remove the recurring template exclusion
    const invoiceWithTransaction = invoice as InvoiceWithTransaction;
    const isRecurringMatch = showRecurring ? invoiceWithTransaction.metadata?.isRecurring : true;
    const isInstallmentMatch = showInstallment ? invoiceWithTransaction.metadata?.isInstallment : true;
    
    // Format search term to handle amount with or without commas
    // e.g., "2300" or "2,300" should match the same invoices
    const searchTermNormalized = searchTerm.replace(/,/g, '');
    
    const matchesSearch = 
      invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customerCompanyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.total?.toString().includes(searchTermNormalized) ||
      (invoice.subtotal || 0).toString().includes(searchTermNormalized);

    const matchesStatus = 
      activeFilter === 'all' ||
      (activeFilter === 'paid' && invoice.status === 'paid') ||
      (activeFilter === 'overdue' && invoice.status === 'overdue') ||
      (activeFilter === 'pending' && invoice.status === 'sent');

    // Date range filtering
    const invoiceDate = parseDateString(invoice.date);
    const now = new Date();
    let matchesDateRange = true;
    
    if (dateRange === '30days') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(now.getDate() - 30);
      matchesDateRange = invoiceDate >= thirtyDaysAgo;
    } else if (dateRange === '60days') {
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(now.getDate() - 60);
      matchesDateRange = invoiceDate >= sixtyDaysAgo;
    } else if (dateRange === '90days') {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(now.getDate() - 90);
      matchesDateRange = invoiceDate >= ninetyDaysAgo;
    } else if (dateRange === 'ytd') {
      const startOfYear = new Date(now.getFullYear(), 0, 1); // January 1st of current year
      matchesDateRange = invoiceDate >= startOfYear;
    } else if (dateRange === 'mtd') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
      matchesDateRange = invoiceDate >= startOfMonth;
    } else if (dateRange === 'qtd') {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const startOfQuarter = new Date(now.getFullYear(), currentQuarter * 3, 1);
      matchesDateRange = invoiceDate >= startOfQuarter;
    } else if (dateRange === 'custom' && startDate && endDate) {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999); // Include all of the end date
      matchesDateRange = invoiceDate >= startDateObj && invoiceDate <= endDateObj;
    }

    // Amount range filtering
    const invoiceTotal = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
    let matchesAmountRange = true;

    if (amountRange === 'under1k') {
      matchesAmountRange = invoiceTotal < 1000;
    } else if (amountRange === '1kto5k') {
      matchesAmountRange = invoiceTotal >= 1000 && invoiceTotal <= 5000;
    } else if (amountRange === 'over5k') {
      matchesAmountRange = invoiceTotal > 5000;
    }

    return matchesSearch && matchesStatus && isRecurringMatch && isInstallmentMatch && matchesDateRange && matchesAmountRange;
  }).sort((a, b) => {
    // Apply sorting based on selected column and order
    if (sortBy === 'date') {
      const dateA = parseDateString(a.date).getTime();
      const dateB = parseDateString(b.date).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    }
    
    if (sortBy === 'amount') {
      const amountA = (a.subtotal || 0) + (a.taxAmount || 0);
      const amountB = (b.subtotal || 0) + (b.taxAmount || 0);
      return sortOrder === 'asc' ? amountA - amountB : amountB - amountA;
    }
    
    if (sortBy === 'customer') {
      const customerA = a.customerCompanyName || a.customerName || '';
      const customerB = b.customerCompanyName || b.customerName || '';
      return sortOrder === 'asc' 
        ? customerA.localeCompare(customerB)
        : customerB.localeCompare(customerA);
    }
    
    // Default sort by date
    const dateA = parseDateString(a.date).getTime();
    const dateB = parseDateString(b.date).getTime();
    return dateB - dateA; // Default to descending date
  });

  // Apply pagination to the filtered invoices
  const totalPages = Math.ceil(filteredInvoices.length / pageSize);
  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilter, showRecurring, showInstallment, dateRange, amountRange]);

  const handleSendInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsEmailDialogOpen(true);
  };

  const handleEditInvoice = (invoiceId: string) => {
    navigate(`/app/invoices/edit/${invoiceId}`);
  };

  const handlePreviewInvoice = (invoiceId: string) => {
    navigate(`/app/invoices/preview?id=${invoiceId}`);
  };

  const handleDeleteClick = (invoice: Invoice) => {
    setInvoiceToDelete(invoice);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteInvoiceMutation.mutateAsync(invoiceToDelete.id);
    } catch (error) {
      // Error deleting invoice
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  const getDeleteWarningMessage = (status: string) => {
    switch (status) {
      case 'sent':
      case 'overdue':
        return "This is a sent invoice. If you delete it, the customer will no longer be able to pay it through the payment link. This action cannot be undone.";
      case 'draft':
        return "Are you sure you want to delete this draft invoice? This action cannot be undone.";
      default:
        return "Are you sure you want to delete this invoice? This action cannot be undone.";
    }
  };

  // Handle refund click
  const handleRefundClick = (invoice: Invoice) => {
    setInvoiceToRefund(invoice as InvoiceWithTransaction);
    setIsRefundDialogOpen(true);
  };

  // Process refund
  const processRefund = async () => {
    if (!invoiceToRefund) return;
    
    setIsProcessingRefund(true);
    
    try {
      // Process refund through NMI
      const refundResult = await processNMIRefund(invoiceToRefund.paymentTransactionId || '');
      
      // Update invoice metadata to reflect refund
      const invoiceRef = doc(db, 'invoices', invoiceToRefund.id);
      await updateDoc(invoiceRef, {
        'metadata.wasRefunded': true,
        'metadata.refundDate': new Date().toISOString(),
        'metadata.refundAmount': invoiceToRefund.total,
        'metadata.statusOverride': 'refunded'
      });
      
      // Update the transaction if it exists
      if (invoiceToRefund.paymentTransactionId) {
        try {
          // Find the transaction by transactionId
          const transactionsRef = collection(db, 'transactions');
          const q = query(transactionsRef, where('transactionId', '==', invoiceToRefund.paymentTransactionId));
          const transactionSnap = await getDocs(q);
          
          // Update the transaction if found
          if (!transactionSnap.empty) {
            const transactionDoc = transactionSnap.docs[0];
            await updateDoc(doc(db, 'transactions', transactionDoc.id), {
              refunded: true,
              refundDate: new Date().toISOString(),
              refundAmount: invoiceToRefund.total
            });
          }
        } catch (e) {
          // Error updating transaction
        }
      }
      
      // Send refund confirmation email if enabled in settings
      try {
        // Get customer details to ensure we have the email
        const customer = await getCustomer(invoiceToRefund.customerId);
        
        // Get company settings to check if refund emails are enabled
        const companySettings = await getCompanySettings(invoiceToRefund.companyId);
        // Company settings for refund email
        
        if (companySettings?.emailSettings?.enableRefundEmail && customer?.email) {
          // Format the date as needed
          const now = new Date();
          
          // Create email content using template if available
          let emailTemplate = companySettings?.emailSettings?.refundIssued || defaultTemplates.refundIssued;
          
          if (emailTemplate) {
            // Replace template variables with actual data
            const templateData = {
              customerName: invoiceToRefund.customerName,
              invoiceNumber: invoiceToRefund.invoiceNumber,
              refundAmount: formatCurrency(invoiceToRefund.total),
              refundDate: now.toLocaleDateString(),
              companyName: companySettings.name || ''
            };
            
            // Replace variables in template
            for (const [key, value] of Object.entries(templateData)) {
              emailTemplate = emailTemplate.replace(new RegExp(`{{${key}}}`, 'g'), value);
            }
            
            // Send email using our API
            const emailResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/emails/send`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                invoice: invoiceToRefund,
                template: emailTemplate,
                subject: companySettings.emailSettings.refundIssuedSubject || 'Refund Processed',
                recipientEmail: customer.email,
                companyId: companySettings.id
              })
            });
            
            if (!emailResponse.ok) {
              throw new Error('Failed to send refund email');
            }
          }
        }
      } catch (e) {
        // Error sending refund email
      }
      
      // Refresh the invoices list
      queryClient.invalidateQueries({ queryKey: ['invoices', effectiveCompanyId] });
      
      // Close the dialog
      setIsRefundDialogOpen(false);
      
      // Show success notification
      showNotification({
        title: "Success",
        message: `Refund of ${formatCurrency(invoiceToRefund.total)} has been processed successfully.`,
        type: "success"
      });
      
    } catch (error) {
      // Refund error
      showNotification({
        title: "Refund Failed",
        message: error instanceof Error ? error.message : "Could not process refund",
        type: "error"
      });
    } finally {
      setIsProcessingRefund(false);
    }
  };

  // Feature switches
  const enableRecurring = companySettings?.enableRecurringInvoices;
  const enableInstallment = companySettings?.enableInstallmentInvoices;

  // Add mutation for toggling isActive
  interface ToggleActiveArgs {
    id: string;
    invoiceType: 'recurring' | 'installment';
    isActive: boolean;
    recurringDetails?: any;
    installmentDetails?: any;
  }
  const toggleActiveMutation = useMutation({
    mutationFn: async (args: ToggleActiveArgs) => {
      const { id, invoiceType, isActive, recurringDetails, installmentDetails } = args;
      if (invoiceType === 'recurring') {
        return updateInvoice(id, { recurringDetails: { ...recurringDetails, isActive } });
      } else if (invoiceType === 'installment') {
        return updateInvoice(id, { installmentDetails: { ...installmentDetails, isActive } });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices', effectiveCompanyId] });
    }
  });

  // Add handleDuplicateInvoice function
  const handleDuplicateInvoice = (invoiceId: string) => {
    navigate(`/app/invoices/duplicate/${invoiceId}`);
  };

  // Add handleDeleteInvoice function
  const handleDeleteInvoice = (invoiceId: string) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      deleteInvoiceMutation.mutate(invoiceId);
    }
  };

  // Add handleDuplicateInvoice function
  if (isLoadingInvoices) {
    return (
      <div className="space-y-6">
        <p className="text-gray-500">Loading invoices...</p>
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[90rem] mx-auto py-8 space-y-8">
      {/* Invoice Tabs */}
      <Tabs 
        value={activeTab} 
        onValueChange={(value) => setActiveTab(value as 'standard' | 'recurring' | 'installment')} 
        className="w-full space-y-6"
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <TabsList className="w-full md:w-auto justify-start">
            <TabsTrigger value="standard">Standard</TabsTrigger>
            <TabsTrigger value="recurring">
              Recurring
              {enableRecurring === false && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-500" />
              )}
            </TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchInvoices()}
              title="Refresh invoices"
            >
              <RefreshCcw className="h-4 w-4" />
            </Button>
            <CreateInvoiceButton companySettings={companySettings} />
          </div>
        </div>
        {/* Standard Invoices Tab */}
        <TabsContent value="standard">
          {/* Modern Search and Filter Bar */}
          <Card className="p-5">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search invoices by number, customer, or amount..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-9 w-full"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                {/* Status Filter */}
                <Select value={activeFilter} onValueChange={(value: any) => handleFilterToggle(value)}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>

                {/* Date Range Filter - Enhanced */}
                <Popover open={isDateRangeOpen} onOpenChange={setIsDateRangeOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[180px]">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span className="flex-1 text-left">
                        {dateRange === 'all' ? 'All Time' : 
                         dateRange === '30days' ? 'Last 30 Days' :
                         dateRange === '60days' ? 'Last 60 Days' :
                         dateRange === '90days' ? 'Last 90 Days' :
                         dateRange === 'ytd' ? 'Year to Date' :
                         dateRange === 'mtd' ? 'Month to Date' :
                         dateRange === 'qtd' ? 'Quarter to Date' :
                         dateRange === 'custom' ? 'Custom Range' : 'All Time'}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="start">
                    <div className="space-y-4">
                      <h4 className="font-medium">Select Date Range</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: 'All Time', value: 'all' },
                          { label: 'Year to Date', value: 'ytd' },
                          { label: 'Month to Date', value: 'mtd' },
                          { label: 'Quarter to Date', value: 'qtd' },
                          { label: 'Last 30 Days', value: '30days' },
                          { label: 'Last 60 Days', value: '60days' },
                          { label: 'Last 90 Days', value: '90days' }
                        ].map((range) => (
                          <Badge
                            key={range.value}
                            variant={dateRange === range.value ? 'default' : 'outline'}
                            className="cursor-pointer py-2 flex justify-center"
                            onClick={() => {
                              setDateRange(range.value as DateRangeType);
                              if (range.value !== 'custom') {
                                setIsDateRangeOpen(false);
                              }
                            }}
                          >
                            {range.label}
                          </Badge>
                        ))}
                      </div>
                      
                      {/* Custom date range picker */}
                      <div className="space-y-2 pt-2 border-t">
                        <label className="text-sm font-medium">Custom Date Range</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <label className="text-xs text-gray-500">From</label>
                            <input 
                              type="date" 
                              className="w-full border rounded p-1 text-sm"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-gray-500">To</label>
                            <input 
                              type="date" 
                              className="w-full border rounded p-1 text-sm"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <Button 
                          className="w-full mt-2" 
                          size="sm"
                          onClick={() => {
                            if (startDate && endDate) {
                              setDateRange('custom');
                              setIsDateRangeOpen(false);
                            } else {
                              showNotification({
                                title: "Date Range Required",
                                message: "Please select both start and end dates",
                                type: "warning"
                              });
                            }
                          }}
                        >
                          Apply Custom Range
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Advanced Filters Button */}
                <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Filter className="h-4 w-4" />
                      More Filters
                      {activeFilters.length > 0 && (
                        <Badge variant="secondary" className="ml-1">
                          {activeFilters.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="end">
                    <div className="space-y-4">
                      <h4 className="font-medium">Advanced Filters</h4>
                      
                      {/* Amount Range */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Amount Range</label>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: 'All', value: 'all' },
                            { label: '< $1K', value: 'under1k' },
                            { label: '$1K-$5K', value: '1kto5k' },
                            { label: '> $5K', value: 'over5k' }
                          ].map((range) => (
                            <Badge
                              key={range.value}
                              variant={amountRange === range.value ? 'default' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => setAmountRange(range.value as any)}
                            >
                              {range.label}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Invoice Type */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Invoice Type</label>
                        <div className="flex flex-wrap gap-2">
                          <Badge
                            variant={showRecurring ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => setShowRecurring(!showRecurring)}
                          >
                            <RepeatIcon className="h-3 w-3 mr-1" />
                            Recurring
                          </Badge>
                        </div>
                      </div>

                      <Separator />

                      {/* Reset and Apply Buttons */}
                      <div className="flex justify-between">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setAmountRange('all');
                            setShowRecurring(false);
                          }}
                        >
                          Reset
                        </Button>
                        <Button size="sm" onClick={() => setIsFilterOpen(false)}>Apply Filters</Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Sort Order Toggle */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="w-10 hidden"
                >
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Active Filters */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap items-center mt-4 gap-2">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {activeFilters.map((filter) => (
                  <Badge
                    key={filter}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {filter}
                    <button
                      onClick={() => removeFilter(filter)}
                      className="ml-1 hover:text-foreground/80"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setActiveFilters([]);
                    setActiveFilter('all');
                    setShowRecurring(false);
                    setAmountRange('all');
                    setDateRange('all');
                    setStartDate('');
                    setEndDate('');
                  }}
                >
                  Clear all
                </Button>
              </div>
            )}
          </Card>

          {/* Enhanced Invoice Table */}
          <TableContainer className="mt-6">
            <EnhancedTable>
              <TableHeader className="bg-slate-50/90">
                <TableRow>
                  <TableHead 
                    className={cn(invoiceTableHeaderClass, 'cursor-pointer hover:bg-slate-100/80')}
                    onClick={() => { setSortBy('date'); setSortOrder(sortBy === 'date' && sortOrder === 'desc' ? 'asc' : 'desc'); }}
                  >
                    <div className="flex items-center">
                      Date
                      {sortBy === 'date' && (
                        <span className="ml-1 text-gray-500">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className={invoiceTableHeaderClass}>Invoice</TableHead>
                  <TableHead 
                    className={cn(invoiceTableHeaderClass, 'cursor-pointer hover:bg-slate-100/80')}
                    onClick={() => { setSortBy('customer'); setSortOrder(sortBy === 'customer' && sortOrder === 'desc' ? 'asc' : 'desc'); }}
                  >
                    <div className="flex items-center">
                      Customer
                      {sortBy === 'customer' && (
                        <span className="ml-1 text-gray-500">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className={cn(invoiceTableHeaderClass, 'text-right cursor-pointer hover:bg-slate-100/80 w-[100px]')}
                    onClick={() => { setSortBy('amount'); setSortOrder(sortBy === 'amount' && sortOrder === 'desc' ? 'asc' : 'desc'); }}
                  >
                    <div className="flex items-center justify-end">
                      Amount
                      {sortBy === 'amount' && (
                        <span className="ml-1 text-gray-500">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className={cn(invoiceTableHeaderClass, 'text-center w-[80px]')}>
                    <div className="flex flex-col items-center">
                      <span>Platform</span>
                      <span>Fee</span>
                    </div>
                  </TableHead>
                  <TableHead className={cn(invoiceTableHeaderClass, 'text-center w-[80px]')}>Status</TableHead>
                  <TableHead className={cn(invoiceTableHeaderClass, 'text-center w-[100px]')}>Type</TableHead>
                  <TableHead className={cn(invoiceTableHeaderClass, 'text-center w-[80px]')}>Autopay</TableHead>
                  <TableHead className={cn(invoiceTableHeaderClass, 'text-center w-[120px]')}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingInvoices ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-12 text-center text-gray-400">
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-10 w-10 mb-2 animate-spin text-gray-300" />
                        <span className="text-lg font-medium">Loading invoices...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-12 text-center text-gray-400">
                      <div className="flex flex-col items-center">
                        <Search className="h-10 w-10 mb-2 text-gray-300" />
                        <span className="text-lg font-medium">No invoices found matching your filters</span>
                        <Button variant="link" className="mt-2" onClick={() => { setSearchTerm(''); setActiveFilter('all'); setActiveFilters([]); setShowRecurring(false); }}>Clear all filters</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInvoices.map((invoice) => {
                    const invoiceWithTransaction = invoice as InvoiceWithTransaction;
                    const isRecurring = invoiceWithTransaction.metadata?.isRecurring;
                    const baseTotalAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
                    const platformFeeAmount = shouldApplyPlatformFee(invoice, companySettings) ? baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
                    
                    // Determine status for enhanced indicator
                    let status = invoice.status;
                    let statusMessage = '';
                    if (invoiceWithTransaction.metadata?.statusOverride === 'refunded') {
                      status = 'cancelled';
                      statusMessage = 'Refunded';
                    } else if (invoice.status === 'overdue') {
                      statusMessage = `${getDaysPastDue(invoice)}d Past Due`;
                    } else {
                      statusMessage = invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1);
                    }

                    const badgeStatusKey = statusMessage === 'Refunded'
                      ? 'refunded'
                      : status;
                    
                    return (
                      <TableRow key={invoice.id} className="hover:bg-blue-50/30 transition-colors">
                        <TableCell className="text-sm">{formatLocalDate(invoice.date)}</TableCell>
                        <TableCell className="text-sm font-medium max-w-[120px]" title={invoice.invoiceNumber}>
                          <div className="truncate">{invoice.invoiceNumber}</div>
                          {invoiceWithTransaction.description && (
                            <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]">
                              {invoiceWithTransaction.description}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm max-w-[160px]" title={invoice.customerCompanyName || invoice.customerName}>
                          {invoice.customerCompanyName ? (
                            <div>
                              <div className="font-medium truncate">{invoice.customerCompanyName}</div>
                              <div className="text-xs text-gray-500 mt-0.5 truncate">{invoice.customerName}</div>
                            </div>
                          ) : (
                            <div className="font-medium truncate">{invoice.customerName}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-right font-medium w-[100px]">
                          {formatCurrency(baseTotalAmount)}
                        </TableCell>
                        <TableCell className="text-sm text-center w-[80px]">
                          {platformFeeAmount > 0 ? (
                            <span className="font-medium text-green-600">
                              {formatCurrency(platformFeeAmount)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center w-[80px]">
                          <Badge
                            className={cn(
                              'px-2.5 py-1 text-[0.75rem] font-semibold min-w-[64px] flex items-center justify-center rounded-none leading-4',
                              getInvoiceStatusBadgeClass(badgeStatusKey)
                            )}
                          >
                            {statusMessage}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center w-[100px]">
                          <div className="flex justify-center gap-1">
                            {isRecurring && (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-0 text-xs">
                                <RepeatIcon className="h-3 w-3 mr-1" />Recurring
                              </Badge>
                            )}
                            {invoice.invoiceType && invoice.invoiceType !== 'standard' && !isRecurring && (
                              <Badge variant="outline" className={cn(
                                "capitalize text-xs",
                                invoice.invoiceType === 'recurring' ? "bg-purple-50 text-purple-700 border-0" :
                                invoice.invoiceType === 'installment' ? "bg-blue-50 text-blue-700 border-0" : ""
                              )}>
                                {invoice.invoiceType}
                              </Badge>
                            )}
                            {/* Display 'Standard' if no other type is set and it's not recurring */}
                            {!isRecurring && (!invoice.invoiceType || invoice.invoiceType === 'standard') && (
                               <Badge variant="outline" className="capitalize bg-gray-50 text-gray-700 border-0 text-xs">
                                Standard
                               </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center w-[80px]">
                          <div className="flex justify-center">
                            <Switch
                              checked={invoice.autoPayEnabled || false}
                              onCheckedChange={async (checked) => {
                                try {
                                  await updateInvoiceMutation.mutateAsync({
                                    id: invoice.id,
                                    invoice: { autoPayEnabled: checked }
                                  });
                                } catch (error) {
                                  showNotification({
                                    title: "Error",
                                    message: "Failed to update autopay setting",
                                    type: "error"
                                  });
                                }
                              }}
                              disabled={invoice.status === 'paid'}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center w-[120px]">
                          <div className="flex justify-center gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 p-1" onClick={async () => {
                                    try {
                                      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/invoices/download?invoiceId=${invoice.id}&companyId=${effectiveCompanyId}`, { method: 'GET', credentials: 'include', headers: { 'Accept': 'application/pdf' }, });
                                      if (!response.ok) throw new Error('Failed to generate PDF');
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.style.display = 'none';
                                      a.href = url;
                                      a.download = `invoice-${invoice.invoiceNumber}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                    } catch (error) {
                                      // Download error
                                      showNotification({ title: 'Download Failed', message: error instanceof Error ? error.message : 'Could not download invoice', type: 'error' });
                                    }
                                  }}>
                                    <Download className="h-3.5 w-3.5 text-gray-600 hover:text-blue-600 transition-colors" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Download invoice</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            {/* Conditional rendering for PAID STANDARD invoices */}
                            {invoice.status === 'paid' && invoice.invoiceType === 'standard' ? (
                              <>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 p-1" onClick={() => handlePreviewInvoice(invoice.id)}>
                                        <Eye className="h-3.5 w-3.5 text-gray-600 hover:text-green-600 transition-colors" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>View invoice</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 p-1" onClick={() => handleRefundClick(invoice)}>
                                        <RefreshCcw className="h-3.5 w-3.5 text-red-600 hover:text-red-700 transition-colors" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Refund invoice</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </>
                            ) : (
                              <>
                                {/* Existing actions for other invoice types/statuses */}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      {/* Edit button for non-paid or non-standard invoices */}
                                      <Button variant="ghost" size="icon" className="h-7 w-7 p-1" onClick={() => handleEditInvoice(invoice.id)}>
                                        <Pencil className="h-3.5 w-3.5 text-blue-600" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit invoice</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 p-1" onClick={() => {
                                        const paymentUrl = `https://secured-payment-processing.com/pay/${invoice.paymentKey || invoice.id}`;
                                        window.open(paymentUrl, '_blank');
                                      }}>
                                        <Eye className="h-3.5 w-3.5 text-gray-600 hover:text-green-600 transition-colors" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>View payment page</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {/* Show delete button only if not paid */}
                                {invoice.status !== 'paid' && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 p-1" onClick={() => handleDeleteClick(invoice)}>
                                          <Trash2 className="h-3.5 w-3.5 text-red-500 hover:text-red-700 transition-colors" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Delete invoice</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {/* Refund button for paid, non-standard invoices (e.g. recurring that are paid) */}
                                {invoice.status === 'paid' && invoice.invoiceType !== 'standard' && (
                                   <TooltipProvider>
                                     <Tooltip>
                                       <TooltipTrigger asChild>
                                         <Button variant="ghost" size="icon" className="h-7 w-7 p-1" onClick={() => handleRefundClick(invoice)}>
                                           <RefreshCcw className="h-3.5 w-3.5 text-amber-600" />
                                         </Button>
                                       </TooltipTrigger>
                                       <TooltipContent>Refund invoice</TooltipContent>
                                     </Tooltip>
                                   </TooltipProvider>
                                )}
                              </>
                            )}
                            {/* Dropdown for more actions - this can be refactored to include some of the above or be conditional */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7 p-1">
                                  <MoreHorizontal className="h-4 w-4 text-gray-600" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleSendInvoice(invoice)}>
                                  <Package className="mr-2 h-4 w-4" /> Send Invoice
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicateInvoice(invoice.id)}>
                                  <Copy className="mr-2 h-4 w-4" /> Duplicate Invoice
                                </DropdownMenuItem>
                                {invoice.invoiceType !== 'standard' && invoice.status !== 'paid' && (
                                  <DropdownMenuItem onClick={() => handleDeleteInvoice(invoice.id)}>
                                    <Trash2 className="mr-2 h-4 w-4" /> Delete Template
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </EnhancedTable>
          </TableContainer>

          {/* Pagination Controls */}
          {filteredInvoices.length > 0 && (
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-4 px-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  Showing {Math.min(filteredInvoices.length, (currentPage - 1) * pageSize + 1)}-
                  {Math.min(filteredInvoices.length, currentPage * pageSize)} of {filteredInvoices.length} invoices
                </span>
                <Select value={pageSize.toString()} onValueChange={(value) => {
                  setPageSize(Number(value));
                  setCurrentPage(1);
                }}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Page size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="25">25 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1 mx-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      // If 5 or fewer pages, show all page numbers
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      // If on page 1-3, show pages 1-5
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // If on last 3 pages, show last 5 pages
                      pageNum = totalPages - 4 + i;
                    } else {
                      // Otherwise show current page and 2 pages before/after
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        className="w-8 h-8 p-0"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
        {/* Recurring Invoices Tab */}
        <TabsContent value="recurring">
          {enableRecurring ? (
            <InvoiceTemplatesTable companySettings={companySettings} />
          ) : (
            <div className="text-center py-8">
              <h3 className="text-lg font-medium text-gray-900">Recurring Invoices Not Enabled</h3>
              <p className="mt-1 text-sm text-gray-500">
                Contact your administrator to enable recurring invoices.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invoice Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            {/* DialogTitle removed as requested */}
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Responsive layout for timeline and preview */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Invoice Timeline - takes 1/4 width on large screens */}
                <div className="lg:col-span-1 bg-white rounded-lg border pl-2 pt-4 pb-4 pr-1 shadow-sm">
                  {selectedInvoice.activities && selectedInvoice.activities.length > 0 ? (
                    <div className="h-[600px] overflow-y-auto overflow-x-hidden w-full">
                      <InvoiceActivityTimeline activities={selectedInvoice.activities} className="px-0.5 pr-1" />
                    </div>
                  ) : (
                    <div className="text-center py-8 px-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
                        <Clock className="h-6 w-6 text-gray-400" />
                      </div>
                      <p className="text-gray-500">No activity recorded for this invoice yet.</p>
                    </div>
                  )}
                </div>
                
                {/* Invoice Preview - takes 3/4 width on large screens */}
                <div className="lg:col-span-3 border rounded-lg overflow-hidden">
                  <div className="h-[600px] overflow-y-auto">
                    <InvoicePreview 
                      invoice={selectedInvoice}
                      companySettings={companySettings || {}}
                      showPreviewWatermark={false}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsPreviewDialogOpen(false)}
                >
                  Close
                </Button>
                {/* Only show Resend Invoice button if status is 'sent' */}
                {selectedInvoice.status === 'sent' && (
                  <Button
                    onClick={() => handleSendInvoice(selectedInvoice)}
                    variant="secondary"
                  >
                    Resend Invoice
                  </Button>
                )}
                {/* Only show Send Invoice button if status is NOT 'sent' and NOT 'paid' */}
                {selectedInvoice.status !== 'sent' && selectedInvoice.status !== 'paid' && (
                  <Button
                    onClick={() => handleSendInvoice(selectedInvoice)}
                  >
                    Send Invoice
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      {selectedInvoice && companySettings && (
        <InvoiceEmailDialog
          open={isEmailDialogOpen}
          onOpenChange={setIsEmailDialogOpen}
          invoice={selectedInvoice}
          companySettings={companySettings}
        />
      )}

      {/* Delete Invoice Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Delete Invoice
            </DialogTitle>
            <DialogDescription>
              {invoiceToDelete && getDeleteWarningMessage(invoiceToDelete.status)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {invoiceToDelete && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{invoiceToDelete && invoiceToDelete.invoiceType === 'recurring' ? 'Template Number:' : 'Invoice Number:'}</span>
                  <span className="font-medium">{invoiceToDelete.invoiceNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-medium">{formatCurrency(invoiceToDelete.total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Customer:</span>
                  <span className="font-medium">{invoiceToDelete.customerName}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteInvoice}
              disabled={isDeleting}
              variant="destructive"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Invoice'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-amber-500" />
              Process Refund
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to refund this invoice? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {invoiceToRefund && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{invoiceToRefund && invoiceToRefund.invoiceType === 'recurring' ? 'Template Number:' : 'Invoice Number:'}</span>
                  <span className="font-medium">{invoiceToRefund.invoiceNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-medium">{formatCurrency(invoiceToRefund.total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Customer:</span>
                  <span className="font-medium">
                    {invoiceToRefund.customerCompanyName || invoiceToRefund.customerName}
                  </span>
                </div>
                {!invoiceToRefund?.paymentTransactionId && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200 text-sm text-yellow-800">
                    Warning: This invoice has no associated transaction ID. Refunding may not be possible.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex space-x-2 sm:space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsRefundDialogOpen(false)}
              disabled={isProcessingRefund}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={processRefund}
              disabled={isProcessingRefund || !invoiceToRefund?.paymentTransactionId}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isProcessingRefund ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm Refund'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
